import { RefObject, useEffect, useRef } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
};

export default function TermsModal({ open, onClose, triggerRef }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      triggerRef.current?.focus();
    };
  }, [open, onClose, triggerRef]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="terms-dialog" role="dialog" aria-modal="true" aria-labelledby="terms-title">
        <header className="terms-header">
          <div><p className="eyebrow">DKSH Scan &amp; Win</p><h2 id="terms-title">Terms &amp; Conditions</h2></div>
          <button ref={closeRef} type="button" className="modal-close" onClick={onClose} aria-label="Terms & Conditions ပိတ်ရန်">×</button>
        </header>
        <div className="terms-content" tabIndex={0}>
          <h3>Campaign Duration &amp; Deadline</h3><h4>ကံစမ်းမဲကာလနှင့် နောက်ဆုံးအချိန်</h4>
          <p>The activity runs from 1st November 2026 to 31st January 2027. The final deadline for entry submission is 31st January 2027 at 11:59 PM. Entries submitted after this exact time will automatically be system-disqualified.</p>
          <p>ဒီလှုပ်ရှားမှုကို ၂၀၂၆ ခုနှစ် အောက်တိုဘာလ ၁ ရက်မှ ဒီဇင်ဘာလ ၃၁ ရက်နေ့အထိ ကျင်းပသွားမည်ဖြစ်ပြီး နောက်ဆုံးပါဝင်နိုင်သည့်အချိန်မှာ ဒီဇင်ဘာလ ၃၁ ရက်၊ ည ၁၁:၅၉ နာရီ ဖြစ်ပါသည်။ သတ်မှတ်ချိန်ထက် ကျော်လွန်သော ပေးပို့မှုများကို ထည့်သွင်းစဉ်းစားမည် မဟုတ်ပါ။</p>

          <h3>Identity Verification</h3><h4>မှတ်ပုံတင် စည်းကမ်း</h4>
          <p>Each participant must provide a valid NRC number. The NRC name must match the registered mobile wallet account name for prize transfer. Each NRC is limited to one winning entry.</p>
          <p>ပါဝင်ကံစမ်းသူ၏ မှတ်ပုံတင်အမည်နှင့် e-Wallet အကောင့်အမည် တူညီရမည်ဖြစ်ပြီး မှတ်ပုံတင်တစ်ခုလျှင် တစ်ကြိမ်သာ ဆုမဲရယူနိုင်မည် ဖြစ်ပါသည်။</p>

          <h3>No Purchase Necessary</h3><h4>ဝယ်ယူရန် မလိုပါ</h4>
          <p>Anyone can enter for free by scanning the QR code on the display stand header/side panel and uploading a photo of the complete display unit.</p>
          <p>မည်သည့်ပစ္စည်းမှ ဝယ်ယူရန် မလိုဘဲ စင်ပေါ်ရှိ QR Code ကို Scan ဖတ်ကာ စင်တစ်ခုလုံး၏ ဓာတ်ပုံကို တင်သွင်းရုံဖြင့် အခမဲ့ ပါဝင်ကံစမ်းနိုင်ပါသည်။</p>

          <h3>Eligibility</h3><h4>ပါဝင်နိုင်သူများ</h4>
          <p>Open to all residents nationwide with a valid NRC. Employees of participating brands and DKSH are not eligible.</p>
          <p>တရားဝင် မှတ်ပုံတင်ရှိသော နိုင်ငံတစ်ဝှမ်းရှိ စားသုံးသူများ ပါဝင်ကံစမ်းနိုင်ပါသည်။ DKSH နှင့် ပူးပေါင်းအမှတ်တံဆိပ် ဝန်ထမ်းများ ပါဝင်ခွင့်မရှိပါ။</p>

          <h3>Prizes</h3><h4>ဆုကြေးများ</h4>
          <ul className="prize-list">
            <li><strong>Grand Prize (ပထမဆု)</strong><span>1,000,000 Ks — 1 Winner</span><span>၁,၀၀၀,၀၀၀ ကျပ် — ၁ ဦး</span></li>
            <li><strong>Consolation Prizes (နှစ်သိမ့်ဆု)</strong><span>100,000 Ks each — 10 Winners</span><span>၁၀၀,၀၀၀ ကျပ် × ၁၀ ဦး</span></li>
          </ul>

          <h3>Photo Validity</h3><h4>ဓာတ်ပုံ စည်းကမ်း</h4>
          <p>The photo must clearly capture the entire official DKSH Myanmar display stand taken within the campaign period. Duplicate or downloaded internet photos will result in immediate disqualification.</p>
          <p>တင်သွင်းသော ဓာတ်ပုံတွင် တရားဝင် DKSH Myanmar အနီရောင်စင်တစ်ခုလုံး ရှင်းလင်းစွာ ပါဝင်ရမည်။ ပုံတူ သို့မဟုတ် အင်တာနက်မှ ကူးယူထားသော ဓာတ်ပုံများ တင်သွင်းပါက မဲစာရင်းမှ ပယ်ဖျက်မည်။</p>

          <h3>Winner Selection &amp; Payout</h3><h4>ဆုမဲရွေးချယ်ခြင်းနှင့် ပေးအပ်ခြင်း</h4>
          <p>Winners will be randomly drawn after the entry period closes on January 31st. Cash prizes will be transferred directly via the chosen e-wallet upon verification.</p>
          <p>ဒီဇင်ဘာလ ၃၁ ရက်နေ့ မဲပိတ်ပြီးနောက် မဲနှိုက်ရွေးချယ်မည်ဖြစ်ပြီး ဆုကြေးငွေများကို e-Wallet မှတစ်ဆင့် တိုက်ရိုက်လွှဲပြောင်းပေးမည်။</p>
        </div>
        <footer className="terms-footer"><button type="button" onClick={onClose}>ပိတ်မည်</button></footer>
      </section>
    </div>
  );
}
